import { noteName } from "../js/music/notes.js";
import { PIANO_NOTE_COUNT, renderPiano } from "../js/music/piano.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";
import { ensureSampler, normalizeSoundfontId, playMidi, renderSoundBankOptions } from "../js/music/soundfonts.js";

const SETTINGS_STORAGE_KEY = "bardsCorner.piano.settings.v1";
const LOWEST_OCTAVE = 1;
const HIGHEST_OCTAVE = 6;
const MIN_ARP_BPM = 30;
const MAX_ARP_BPM = 260;
const DEFAULT_ARP_BPM = 120;
const DEFAULT_ARP_NOTE_LENGTH = "1/8";
const DEFAULT_SUSTAIN = 1;
const ARP_NOTE_LENGTHS = {
  "1/16": 0.25,
  "1/8": 0.5,
  "1/4": 1,
  "1/2": 2,
};
const KEYBOARD_BINDINGS = [
  ["KeyA", "A"], ["KeyW", "W"], ["KeyS", "S"], ["KeyE", "E"], ["KeyD", "D"], ["KeyF", "F"],
  ["KeyT", "T"], ["KeyG", "G"], ["KeyY", "Y"], ["KeyH", "H"], ["KeyU", "U"], ["KeyJ", "J"],
  ["KeyK", "K"], ["KeyO", "O"], ["KeyL", "L"], ["KeyP", "P"], ["Semicolon", ";"], ["Quote", "'"],
];
const KEYBOARD_LAYOUT_ROWS = [
  [
    ["KeyW"], ["KeyE"], null, ["KeyT"], ["KeyY"], ["KeyU"], null, ["KeyO"], ["KeyP"],
  ],
  [
    ["KeyA"], ["KeyS"], ["KeyD"], ["KeyF"], ["KeyG"], ["KeyH"], ["KeyJ"], ["KeyK"], ["KeyL"], ["Semicolon"], ["Quote"],
  ],
  [
    ["KeyZ", "Z", "Octave Down"], ["KeyX", "X", "Octave Up"],
  ],
];
const DEFAULT_STATE = {
  arpBpm: DEFAULT_ARP_BPM,
  arpEnabled: false,
  arpMode: "up",
  arpNoteLength: DEFAULT_ARP_NOTE_LENGTH,
  lowestOctave: 3,
  soundBank: "acoustic_grand_piano",
  sustain: DEFAULT_SUSTAIN,
};
const state = { ...DEFAULT_STATE };
const heldNotes = new Map();
const keyboardHeld = new Map();
const pointerHeld = new Map();
let arpTimer = null;
let arpIndex = 0;
let heldSequence = 0;
let soundBankWarmupId = 0;

function $(id) {
  return document.getElementById(id);
}

function loadSettings() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SETTINGS_STORAGE_KEY) || "{}");
    state.arpBpm = normalizeArpBpm(saved.arpBpm);
    state.arpEnabled = saved.arpEnabled === true;
    state.arpMode = normalizeArpMode(saved.arpMode);
    state.arpNoteLength = normalizeArpNoteLength(saved.arpNoteLength);
    state.lowestOctave = normalizeOctave(saved.lowestOctave, DEFAULT_STATE.lowestOctave);
    state.soundBank = normalizeSoundfontId(saved.soundBank, DEFAULT_STATE.soundBank);
    state.sustain = normalizeSustain(saved.sustain);
  } catch {
    state.arpBpm = DEFAULT_STATE.arpBpm;
    state.arpEnabled = DEFAULT_STATE.arpEnabled;
    state.arpMode = DEFAULT_STATE.arpMode;
    state.arpNoteLength = DEFAULT_STATE.arpNoteLength;
    state.lowestOctave = DEFAULT_STATE.lowestOctave;
    state.soundBank = DEFAULT_STATE.soundBank;
    state.sustain = DEFAULT_STATE.sustain;
  }
}

function saveSettings() {
  window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({
    arpBpm: state.arpBpm,
    arpEnabled: state.arpEnabled,
    arpMode: state.arpMode,
    arpNoteLength: state.arpNoteLength,
    lowestOctave: state.lowestOctave,
    soundBank: state.soundBank,
    sustain: state.sustain,
  }));
}

function normalizeArpBpm(value) {
  const bpm = Number(value);
  if (!Number.isFinite(bpm)) {
    return DEFAULT_ARP_BPM;
  }
  return Math.min(MAX_ARP_BPM, Math.max(MIN_ARP_BPM, Math.round(bpm)));
}

function normalizeArpMode(value) {
  return ["up", "down", "upDown", "orderPressed"].includes(value) ? value : DEFAULT_STATE.arpMode;
}

function normalizeArpNoteLength(value) {
  return Object.hasOwn(ARP_NOTE_LENGTHS, value) ? value : DEFAULT_ARP_NOTE_LENGTH;
}

function normalizeOctave(value, fallback = DEFAULT_STATE.lowestOctave) {
  const octave = Number(value);
  if (!Number.isInteger(octave)) {
    return fallback;
  }
  return Math.min(HIGHEST_OCTAVE, Math.max(LOWEST_OCTAVE, octave));
}

function normalizeSustain(value) {
  const sustain = Number(value);
  return Number.isFinite(sustain) ? Math.max(0.25, Math.min(2, sustain)) : DEFAULT_SUSTAIN;
}

function formatSustain() {
  return `${Math.round(state.sustain * 100)}%`;
}

function midiForLowestOctave(octave = state.lowestOctave) {
  return (normalizeOctave(octave) + 1) * 12;
}

function visibleMidiForKeyCode(code) {
  const keyIndex = KEYBOARD_BINDINGS.findIndex(([bindingCode]) => bindingCode === code);
  if (keyIndex === -1) {
    return null;
  }
  return midiForLowestOctave() + keyIndex;
}

function keyElementForMidi(midi) {
  return $("piano")?.querySelector(`.piano-key[data-midi="${midi}"]`) || null;
}

function midiFromEventPoint(event) {
  const element = document.elementFromPoint(event.clientX, event.clientY);
  const keyElement = element?.closest?.(".piano-key");
  if (!keyElement || !$("piano").contains(keyElement)) {
    return null;
  }
  const midi = Number(keyElement.dataset.midi);
  return Number.isInteger(midi) ? midi : null;
}

function renderOctaveOptions() {
  $("octaveSelect").replaceChildren(...Array.from({ length: HIGHEST_OCTAVE - LOWEST_OCTAVE + 1 }, (_unused, index) => {
    const octave = LOWEST_OCTAVE + index;
    const option = document.createElement("option");
    option.value = String(octave);
    option.textContent = `C${octave}`;
    return option;
  }));
}

function playPianoNote(midi, keyElement = keyElementForMidi(midi), duration = 0.8) {
  playMidi(midi, {
    soundBank: state.soundBank,
    duration: Math.max(0.05, duration * state.sustain),
    triggerElement: keyElement,
  }).catch((error) => {
    window.console.error(error);
  });
}

function warmSoundBank(soundBank = state.soundBank) {
  const warmupId = ++soundBankWarmupId;
  ensureSampler(soundBank).catch((error) => {
    if (warmupId === soundBankWarmupId) {
      window.console.warn("Could not warm piano soundfont.", error);
    }
  });
}

function renderKeyboard() {
  renderPiano($("piano"), (midi, keyElement) => {
    if (state.arpEnabled) {
      return;
    }
    playPianoNote(midi, keyElement);
  }, {
    startMidi: midiForLowestOctave(),
  });
  renderKeyboardBindings();
  renderHeldNotes();
}

function renderOctaveControls() {
  $("octaveSelect").value = String(state.lowestOctave);
  $("octaveDownButton").disabled = state.lowestOctave <= LOWEST_OCTAVE;
  $("octaveUpButton").disabled = state.lowestOctave >= HIGHEST_OCTAVE;
}

function renderArpeggiatorControls() {
  $("arpToggleButton").textContent = state.arpEnabled ? "Arp On" : "Arp Off";
  $("arpToggleButton").classList.toggle("btn-warning", state.arpEnabled);
  $("arpToggleButton").classList.toggle("btn-outline-warning", !state.arpEnabled);
  $("arpToggleButton").setAttribute("aria-pressed", String(state.arpEnabled));
  $("arpModeSelect").value = state.arpMode;
  $("arpBpmInput").value = String(state.arpBpm);
  $("arpNoteLengthSelect").value = state.arpNoteLength;
}

function renderTransportSettings() {
  $("sustainRange").value = String(state.sustain);
  $("sustainValue").textContent = formatSustain();
}

function renderKeyboardBindings() {
  const startMidi = midiForLowestOctave();
  const noteByCode = new Map(KEYBOARD_BINDINGS.map(([code, key], index) => [
    code,
    { key, label: noteName(startMidi + index) },
  ]));
  const rows = KEYBOARD_LAYOUT_ROWS.map((layoutRow, rowIndex) => {
    const row = document.createElement("div");
    row.className = `keyboard-bindings-row keyboard-bindings-row-${rowIndex + 1}`;
    layoutRow.forEach((item) => {
      if (!item) {
        const spacer = document.createElement("span");
        spacer.className = "binding-chip-spacer";
        row.append(spacer);
        return;
      }
      const [code, actionKey, actionLabel] = item;
      const binding = noteByCode.get(code);
      const chip = document.createElement("span");
      chip.className = `binding-chip${binding ? "" : " binding-chip-action"}`;
      chip.innerHTML = `<kbd></kbd><span></span>`;
      chip.querySelector("kbd").textContent = binding?.key || actionKey;
      chip.querySelector("span").textContent = binding?.label || actionLabel;
      row.append(chip);
    });
    return row;
  });
  $("keyboardBindings").replaceChildren(...rows);
}

function renderHeldNotes() {
  const notes = orderedHeldMidis();
  $("arpHeldNotes").textContent = notes.length
    ? `Held: ${notes.map((midi) => noteName(midi)).join(" ")}`
    : "Hold notes to arpeggiate";
}

function setLowestOctave(octave) {
  state.lowestOctave = normalizeOctave(octave, state.lowestOctave);
  clearHeldNotes();
  renderKeyboard();
  renderOctaveControls();
  saveSettings();
}

function setArpEnabled(enabled) {
  state.arpEnabled = Boolean(enabled);
  arpIndex = 0;
  renderArpeggiatorControls();
  syncArpeggiatorTimer();
  saveSettings();
}

function setArpMode(mode) {
  state.arpMode = normalizeArpMode(mode);
  arpIndex = 0;
  renderArpeggiatorControls();
  saveSettings();
}

function setArpBpm(value) {
  state.arpBpm = normalizeArpBpm(value);
  renderArpeggiatorControls();
  syncArpeggiatorTimer();
  saveSettings();
}

function setArpNoteLength(value) {
  state.arpNoteLength = normalizeArpNoteLength(value);
  renderArpeggiatorControls();
  syncArpeggiatorTimer();
  saveSettings();
}

function setSustain(value) {
  state.sustain = normalizeSustain(value);
  renderTransportSettings();
  saveSettings();
}

function addHeldNote(midi) {
  if (!Number.isInteger(midi)) {
    return;
  }
  const existing = heldNotes.get(midi);
  heldNotes.set(midi, {
    count: (existing?.count || 0) + 1,
    order: existing?.order ?? heldSequence,
  });
  heldSequence += existing ? 0 : 1;
  arpIndex = 0;
  renderHeldNotes();
}

function removeHeldNote(midi) {
  const existing = heldNotes.get(midi);
  if (!existing) {
    return;
  }
  if (existing.count > 1) {
    heldNotes.set(midi, { ...existing, count: existing.count - 1 });
  } else {
    heldNotes.delete(midi);
  }
  arpIndex = 0;
  renderHeldNotes();
}

function clearHeldNotes() {
  heldNotes.clear();
  keyboardHeld.clear();
  pointerHeld.clear();
  arpIndex = 0;
  renderHeldNotes();
}

function orderedHeldMidis() {
  return [...heldNotes.entries()]
    .sort(([, left], [, right]) => left.order - right.order)
    .map(([midi]) => midi);
}

function arpeggiatorMidis() {
  const ordered = orderedHeldMidis();
  if (state.arpMode === "orderPressed") {
    return ordered;
  }
  const sorted = [...ordered].sort((left, right) => left - right);
  if (state.arpMode === "down") {
    return sorted.reverse();
  }
  if (state.arpMode !== "upDown" || sorted.length < 3) {
    return sorted;
  }
  return [...sorted, ...sorted.slice(1, -1).reverse()];
}

function arpStep() {
  if (!state.arpEnabled) {
    return;
  }
  const sequence = arpeggiatorMidis();
  if (!sequence.length) {
    return;
  }
  const midi = sequence[arpIndex % sequence.length];
  arpIndex += 1;
  playPianoNote(midi, keyElementForMidi(midi), Math.max(0.08, arpStepSeconds() * 0.82));
}

function arpStepSeconds() {
  return (60 / state.arpBpm) * ARP_NOTE_LENGTHS[state.arpNoteLength];
}

function syncArpeggiatorTimer() {
  window.clearInterval(arpTimer);
  arpTimer = null;
  if (!state.arpEnabled) {
    return;
  }
  arpTimer = window.setInterval(arpStep, arpStepSeconds() * 1000);
}

function isEditableTarget(target) {
  return Boolean(target?.closest?.("input, textarea, [contenteditable='true']"));
}

function handleGlobalKeydown(event) {
  if (event.ctrlKey || event.metaKey || event.altKey || event.repeat || isEditableTarget(event.target)) {
    return;
  }
  if (event.code === "KeyZ" || event.code === "KeyX") {
    event.preventDefault();
    event.stopImmediatePropagation();
    setLowestOctave(state.lowestOctave + (event.code === "KeyZ" ? -1 : 1));
    return;
  }
  const midi = visibleMidiForKeyCode(event.code);
  if (midi === null || midi >= midiForLowestOctave() + PIANO_NOTE_COUNT) {
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  if (keyboardHeld.has(event.code)) {
    return;
  }
  keyboardHeld.set(event.code, midi);
  addHeldNote(midi);
  if (!state.arpEnabled) {
    playPianoNote(midi);
  }
}

function handleGlobalKeyup(event) {
  const midi = keyboardHeld.get(event.code);
  if (!Number.isInteger(midi)) {
    return;
  }
  keyboardHeld.delete(event.code);
  removeHeldNote(midi);
}

function bindPointerTracking() {
  const piano = $("piano");
  piano.addEventListener("pointerdown", (event) => {
    const midi = midiFromEventPoint(event);
    if (!Number.isInteger(midi)) {
      return;
    }
    pointerHeld.set(event.pointerId, midi);
    addHeldNote(midi);
  }, { capture: true });
  piano.addEventListener("pointermove", (event) => {
    const previousMidi = pointerHeld.get(event.pointerId);
    if (!Number.isInteger(previousMidi)) {
      return;
    }
    const nextMidi = midiFromEventPoint(event);
    if (!Number.isInteger(nextMidi) || nextMidi === previousMidi) {
      return;
    }
    removeHeldNote(previousMidi);
    pointerHeld.set(event.pointerId, nextMidi);
    addHeldNote(nextMidi);
  }, { capture: true });
  ["pointerup", "pointercancel", "lostpointercapture"].forEach((eventName) => {
    piano.addEventListener(eventName, (event) => {
      const midi = pointerHeld.get(event.pointerId);
      if (!Number.isInteger(midi)) {
        return;
      }
      pointerHeld.delete(event.pointerId);
      removeHeldNote(midi);
    }, { capture: true });
  });
}

function init() {
  loadSettings();
  renderSoundBankOptions($("soundBankSelect"), state.soundBank);
  renderOctaveOptions();
  renderKeyboard();
  renderOctaveControls();
  renderArpeggiatorControls();
  renderTransportSettings();
  syncArpeggiatorTimer();

  $("soundBankSelect").addEventListener("change", (event) => {
    state.soundBank = normalizeSoundfontId(event.target.value, state.soundBank);
    event.target.value = state.soundBank;
    playSoundfontPreview("fatboy", state.soundBank);
    warmSoundBank();
    saveSettings();
  });
  $("octaveDownButton").addEventListener("click", () => setLowestOctave(state.lowestOctave - 1));
  $("octaveUpButton").addEventListener("click", () => setLowestOctave(state.lowestOctave + 1));
  $("octaveSelect").addEventListener("change", (event) => setLowestOctave(event.target.value));
  $("arpToggleButton").addEventListener("click", () => setArpEnabled(!state.arpEnabled));
  $("arpModeSelect").addEventListener("change", (event) => setArpMode(event.target.value));
  $("arpBpmInput").addEventListener("change", (event) => setArpBpm(event.target.value));
  $("arpBpmInput").addEventListener("blur", (event) => setArpBpm(event.target.value));
  $("arpNoteLengthSelect").addEventListener("change", (event) => setArpNoteLength(event.target.value));
  $("sustainRange").addEventListener("input", (event) => setSustain(event.target.value));
  $("transportSettingsButton").addEventListener("click", () => window.bootstrap.Modal.getOrCreateInstance($("transportModal")).show());
  bindPointerTracking();
  document.addEventListener("keydown", handleGlobalKeydown, { capture: true });
  document.addEventListener("keyup", handleGlobalKeyup, { capture: true });
  window.addEventListener("blur", clearHeldNotes);
  window.setTimeout(() => warmSoundBank(), 0);
}

init();
